import React from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { Done } from 'emotion-icons/material'
import { StaticImage } from 'gatsby-plugin-image'
import { Layout, Head as HeadComponent } from '@leshen/gatsby-theme-sylex'
import BroadbandNutritionLabels from '../components/BroadbandNutritionLabels'
import { Dropdown, List, ListItem, Typography } from '@leshen/ui'
import { graphql, navigate, withPrefix } from 'gatsby'
import { createPageData } from '../utils/page'
import { metadata } from '../data/metadata'
import { cityFaq } from '../data/faq'
import styled from '@emotion/styled'

import {
  Main,
  Accordion,
  Column,
  Columns,
  Hero,
  LeshenPhoneCTA,
  LinkButton,
  SplitContent,
  Stack,
  useMapi,
  VariableContent,
} from '@leshen/gatsby-theme-leshen'

export function Head({ data }) {
  const node = data.pageInfo.edges[0].node
  return (
    <HeadComponent
      pageContext={{ page: createPageData(node) }}
      data={{ site: { siteMetadata: metadata } }}
    />
  )
}

function StateTemplate({ data }) {
  const { rotatedNumber } = useMapi()
  const node = data.pageInfo.edges[0].node
  const { State, City } = node
  const cityDropdownData = data.cities.edges.map((current) => ({
    label: current.node.City,
    value: current.node.Page_Path,
  })).sort((a, b) => a.label.localeCompare(b.label))

  return (
    <Layout
      page={createPageData(node)}
      siteMetadata={metadata}
      header={<Header />}
      footer={
        <Footer 
          disclaimers={[
            data.footerDisclaimerOne,
            data.footerDisclaimerTwo,
            data.footerDisclaimerThree,
            data.footerDisclaimerFour,
            data.footerDisclaimerFive,
            data.footerDisclaimerSix,
            data.footerDisclaimerSeven,
            data.footerDisclaimerEight,
            data.footerDisclaimerNine,
          ]} 
        />
      }
      main={
        <Main>
          <Hero
            textColor="light"
            backgroundColor="secondary"
            mobileAlignImageToBottom
            className="center-mobile-image"
            image={
              <StaticImage
                src="../images/hero/hero.webp"
                alt="Friends using a laptop outdoors"
                placeholder="blurred"
                loading="eager"
              />
            }
            mobileImage={
              <StaticImage
                src="../images/hero/hero-mobile.webp"
                alt="Friends using a laptop outdoors"
                placeholder="blurred"
                loading="eager"
              />
            }
          >
            <Typography variant="h1" color="light">
              Hughesnet<sup>®</sup> Is Available in {City}
            </Typography>
            <Typography variant="h5" color="light">
              Your wait for high-speed internet is over. Shop Hughesnet in{' '}
              {City} for speeds up to 100 Mbps.<sup>**</sup>
            </Typography>
            <LeshenPhoneCTA
              cobra="Hide CTA"
              variant="hero"
              color="white"
              leftIcon={false}
              style={{ marginBottom: '20px' }}
            >
              Order Hughesnet at
            </LeshenPhoneCTA>
            <LinkButton
              variant="hero"
              color="primary"
              to="https://www.hughesnet.com/?campaignid=289160&phone_number=877-891-6641&utm_campaign=clearlinkutahseo&utm_medium=referral&utm_source=partner&partnerid=289160&programcode=289160KEPCJ3&request_id=%7BREQUEST_ID%7D&kbid=%7BPROMO_CODE%7D"
            >
              Order Online
            </LinkButton>
          </Hero>

          <SplitContent
            className="reverse"
            mobileAlignImageToBottom
            backgroundColor="White"
            image={
              <StaticImage
                src="../images/lifestyle/outdoors.webp"
                alt="A person woking on their laptop outdoors."
                placeholder="blurred"
              />
            }
            mainContent={
              <>
                <Typography variant="h2">
                  Why Hughesnet Satellite Internet in {City}?
                </Typography>
                <Typography variant="">
                  Connect, stream, and play like never before with the new
                  Hughesnet in {City}—especially in areas with low-latency
                  Fusion plans. No other provider in America combines satellite
                  and wireless technologies to reduce lag time and create a more
                  responsive experience for rural customers.
                </Typography>
                <List type="unordered">
                  <ListItem icon={<Done />}>
                    <strong>
                      Speeds up to 100 Mbps<sup>**</sup>
                    </strong>{' '}
                    for work, streaming, gaming
                  </ListItem>
                  <ListItem icon={<Done />}>
                    <strong>
                      Unlimited Standard Data<sup>1</sup>
                    </strong>{' '}
                    for service all month
                  </ListItem>
                  <ListItem icon={<Done />}>
                    <strong>Up to 200 GB</strong> Priority Data for top-speed
                    activities
                  </ListItem>
                  <ListItem icon={<Done />}>
                    <strong>Advanced Wi-Fi</strong> with optional extenders
                  </ListItem>
                  <ListItem icon={<Done />}>
                    <strong>New low-latency Fusion plans</strong> available in
                    select areas
                  </ListItem>
                </List>
                <br />
                <LeshenPhoneCTA
                  cobra="Swap with Cobra CTA"
                  color="primary"
                  leftIcon={false}
                >
                  Order Hughesnet at
                </LeshenPhoneCTA>
              </>
            }
          />
          <VariableContent
            mainContent={
              <>
                <Typography variant="h2">
                  Search Hughesnet Availability Map
                </Typography>
                <Typography>
                  Check the nationwide Hughesnet availability map here or select
                  another city to view Hughesnet near you.
                </Typography>
                <Dropdown
                  options={cityDropdownData}
                  onChange={(e) => navigate(e.value)}
                  placeholderText="Select City"
                />
                {cityDropdownData?.map((current) => (
                  <StyledLink
                    href={withPrefix(current.value)}
                    key={current.value}
                  >
                    {current.label}
                  </StyledLink>
                ))}
                <br />
                <br />
                <StaticImage
                  src="../images/splitContent/map.webp"
                  alt="Hughesnet Availablity US Map"
                  placeholder="blurred"
                />
              </>
            }
            alignMainContent="Center"
          />
          <VariableContent
            mainContent={
              <>
                <Typography variant="h2">
                  Bundle Hughesnet Internet in {City}
                </Typography>
                <Typography variant="body">
                  Add a few tech accessories to hedge your bets and protect your
                  assets. Promotional offers available!
                </Typography>
              </>
            }
            backgroundColor=""
            alignMainContent="center"
          >
            <Stack spacing="xxl">
              <Columns>
                <Column backgroundColor="primary">
                  <Typography variant="h5">
                    <strong>
                      Norton<sup>TM</sup> 360 Deluxe
                    </strong>
                  </Typography>
                  <Typography variant="body">
                    <strong>
                      FREE 1 month<sup>†</sup>
                    </strong>
                  </Typography>
                  <Typography variant="body">
                    Antivirus, cloud backup<sup>2</sup>, VPN, password manager,
                    and more on up to 10 devices.
                  </Typography>
                </Column>
                <Column backgroundColor="primary">
                  <Typography variant="h5">
                    <strong>Hughesnet Voice</strong>
                  </Typography>
                  <Typography variant="body">
                    <strong>
                      $20/mo. off for 3 months<sup>‡</sup>, FREE equipment ($75
                      value)<sup>‡‡</sup>
                    </strong>
                  </Typography>
                  <Typography variant="body">
                    Home phone service with unlimited domestic calling.
                  </Typography>
                </Column>
                <Column backgroundColor="primary">
                  <Typography variant="h5">
                    <strong>Express Repair</strong>
                  </Typography>
                  <Typography variant="body">
                    <strong>
                      FREE 1 month<sup>††</sup>
                    </strong>
                  </Typography>
                  <Typography variant="body">
                    Expedited appointments for any technical problems we can’t
                    resolve over the phone.
                  </Typography>
                </Column>
              </Columns>
            </Stack>
          </VariableContent>
          <VariableContent
            mainContent={
              <Typography variant="h2">Frequently asked questions</Typography>
            }
            alignMainContent="center"
          >
            <Stack spacing="xxl" alignMainContent="center">
              <Accordion items={cityFaq({ City, State, rotatedNumber })} />
            </Stack>
          </VariableContent>

          <BroadbandNutritionLabels />

        </Main>
      }
    />
  )
}

export default StateTemplate

const StyledLink = styled.a`
  display: none !important;
`

export const query = graphql`
  query StateTemplateQuery($pagePath: String!, $stateAbbreviation: String!) {
    site {
      ...LeshenMetadata
    }
    pageInfo: allDatasetManagerHughesnetAoa(
      filter: { Page_Path: { eq: $pagePath } }
    ) {
      edges {
        node {
          State
          City
          Promo_Code
          Page_Path
          Meta_Title
          Meta_Description
        }
      }
    }
    cities: allDatasetManagerHughesnetAoa(
      filter: {
        State_Abbreviation: { eq: $stateAbbreviation }
        Area_Type: { eq: "city" }
      }
    ) {
      edges {
        node {
          City
          Page_Path
        }
      }
    }
    footerDisclaimerOne: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-footer1" }
    ) {
      symbol
      text
    }
    footerDisclaimerTwo: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-footer2" }
    ) {
      symbol
      text
    }
    footerDisclaimerThree: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-footer3" }
    ) {
      symbol
      text
    }
    footerDisclaimerFour: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-norton360-onemonthfree" }
    ) {
      symbol
      text
    }
    footerDisclaimerFive: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-norton360-cloudbackup" }
    ) {
      symbol
      text
    }
    footerDisclaimerSix: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-expressrepair-onemonthfree" }
    ) {
      symbol
      text
    }
    footerDisclaimerSeven: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-voice-freeequipment" }
    ) {
      symbol
      text
    }
    footerDisclaimerEight: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-termrequired" }
    ) {
      symbol
      text
    }
    footerDisclaimerNine: brandyDisclaimer(
      brandy_id: { eq: "disclaimer-hn-save25promo" }
    ) {
      symbol
      text
    }
  }
`
